import { useApiClient } from "@hostingbv/lib/src/api";
import { CopyableInput } from "@hostingbv/lib/src/components/form/Input";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { FC, useEffect, useState } from "react";
import { config } from "../config";
import { generatePassword } from "@hostingbv/lib/src/util/generatePassword";
import { Button } from "@hostingbv/lib/src/components/form/Button";
import { useModalControls } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { useRefresh } from "@hostingbv/lib/src/components/RefreshController";
import { Toggle } from "../../../lib/src/components/form/Toggle";
import { Customer } from "@hostingbv/lib/src/api/common/models/Customer";
import { Contact } from "@hostingbv/lib/src/api/common/models/Contact";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { RoleSelect } from "../components/RoleSelect";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";

export const InviteCustomerContactModal: FC<{ customer: Customer, contact: Contact }> = props => {
  const [state, setState] = useState<'initial' | 'granting' | 'copy_password' | 'ready'>(props.contact.hasUser ? 'copy_password' : 'initial')
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!
  const { partnerClient } = useApiClient(config)
  const { close } = useModalControls()
  const refresh = useRefresh()
  const [password, setPassword] = useState('')
  const [resetMFA, setResetMFA] = useState<boolean>(false)
  const [roleUuid, setRoleUuid] = useState('')
  const [error, setError] = useState('')
  const { can } = usePermissions(partnerClient)
  // Without permission to view roles no role is sent, the backend then keeps the current role or uses the default.
  const canSelectRole = can("view", "roles")
  useEffect(() => {
    setPassword(generatePassword())
  }, [])

  const sendInvite = async () => {
    setError('')
    const result = await partnerClient.inviteCustomerContact(props.customer.id, props.contact.id, canSelectRole && roleUuid !== '' ? roleUuid : undefined)
    if (!result.success) {
      setError(result.error)
      return
    }
    // The login is created by a deployment. Wait for it, so the password below can be set and the contact list
    // shows the login once the modal is closed.
    setState('granting')
    const deployment = await awaitNextDeploymentFinished(result.data.deployment.id)
    if (deployment?.status === "failed") {
      // @ts-expect-error output is untyped
      setError(deployment.output?.message ?? 'Could not grant login access')
      setState('initial')
      return
    }
    if (result.data.newUser) {
      setState('copy_password')
    } else {
      setState('ready')
    }
  }
  const resetPassword = async () => {
    await partnerClient.resetCustomerContactPassword(props.customer.id, props.contact.id, password)
    if (resetMFA) {
      await partnerClient.resetCustomerUserMFA(props.customer.id, props.contact.id)
    }
    setState('ready')
  }

  const done = () => {
    refresh()
    close()
  }

  return <>
    <div className={"space-y-4 flex-1 pb-4"}>
      {state === 'initial' && <>
        <Paragraph>
          If the user does not have access to other customers yet, a password will be generated for them.
        </Paragraph>
        <Paragraph>
          If the user has already has access to another customer, they will be able to use their existing password.
        </Paragraph>
        {canSelectRole && <>
          <RoleSelect value={roleUuid} onChange={setRoleUuid} preselect={props.contact.role?.id} />
          <Paragraph>
            The role determines what the user can see and do. It belongs to the login, so if the user already has
            access to another customer, the selected role applies there too.
          </Paragraph>
        </>}
        {error.length > 0 && <div className={"font-medium text-red-600"}>
          {error}
        </div>}
        <Button type={'primary'} size={'sm'} text={'Invite user'} onClick={() => sendInvite()} />
      </>}

      {state === 'granting' && <>
        <Paragraph>
          Granting access...
        </Paragraph>
      </>}

      {state === 'copy_password' && <>
        <Paragraph>
          You can share this temporary password with the user, they will be prompted to change it on their first login.
        </Paragraph>
        <CopyableInput label={"Temporary password"} value={password} />
        <Paragraph>
          Along with resetting the password you also may reset the multi factor authentication of the user, they will be prompted add it again on their first login.
        </Paragraph>
        <Toggle label={'Reset MFA'} value={resetMFA} onChange={setResetMFA} />
        <Button type={'primary'} size={'sm'} text={'Set password & continue'} onClick={() => resetPassword()} />
      </>}
      {state === 'ready' && <>
        <Paragraph>
          Access granted!
        </Paragraph>
        <Button type={'primary'} size={'sm'} text={'Close'} onClick={() => done()} />
      </>}
    </div>
  </>
}

